import { Trans, useLingui } from '@lingui/react/macro';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateGroupNameConflict } from 'modules/manager/domain/template-name-conflict';
import { useCopyMediaManagerTemplateGroupMutation } from 'modules/manager/store/media-manager-api';
import MediaManagerTemplateGroupForm, {
  TEMPLATE_GROUP_FORM_INITIAL_VALUES,
  TemplateGroupFormValues,
} from 'modules/manager/ui/components/forms/media-manager-template-group-form';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';

interface Args {
  existingNames: string[];
  onDuplicated: (copy: MediaManagerTemplateGroup) => void;
}

/** The organization's group list refetches by the mutation's tags; `onDuplicated` only navigates (IMGM-1515). */
function useDuplicateTemplateGroupModal({ existingNames, onDuplicated }: Args) {
  const { t } = useLingui();
  const tournamentId = useSelector(authStoreUserTournamentId);
  const [copyGroup, { isLoading }] = useCopyMediaManagerTemplateGroupMutation();
  const [group, setGroup] = useState<MediaManagerTemplateGroup | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((next: MediaManagerTemplateGroup) => {
    setError(null);
    setGroup(next);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(
    async (values: TemplateGroupFormValues) => {
      if (!group || !tournamentId) return;
      setError(null);
      try {
        const name = values.name.trim() || group.name;
        const copy = await copyGroup({
          templateGroupId: group.id,
          tournamentId,
          name,
        }).unwrap();
        close();
        onDuplicated(copy);
      } catch (err) {
        console.error('Failed to duplicate media manager template group:', err);
        setError(
          isTemplateGroupNameConflict(err) ? t(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось дублировать группу, попробуйте ещё раз`,
        );
      }
    },
    [group, tournamentId, copyGroup, onDuplicated, close, t],
  );

  return {
    open,
    close,
    canDuplicate: !!tournamentId,
    isDuplicating: isLoading,
    modal: (
      <Modal show={isOpen} onClose={isLoading ? () => undefined : close} size="md">
        <UDText type="title-3" className="mb-5">
          <Trans>Дублировать группу</Trans>
        </UDText>
        <MediaManagerTemplateGroupForm
          key={group?.id ?? 'none'}
          initialValues={{ ...TEMPLATE_GROUP_FORM_INITIAL_VALUES, name: group?.name ?? '' }}
          existingNames={existingNames}
          isLoading={isLoading}
          errorMessage={error}
          submitLabel={<Trans>Дублировать</Trans>}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useDuplicateTemplateGroupModal;
